import { useState, useRef } from 'react'
import {
  usePackages,
  useCreatePackage,
  useDeletePackage,
  useUploadArtifact,
  useRollouts,
  useCreateRollout,
  useStartRollout,
  useCancelRollout,
  useRolloutProgress,
  type RolloutProgress,
} from '@/api/packages'
import { useFleets } from '@/api/devices'
import { useDeviceTypes } from '@/api/admin'
import { RequirePermission } from '@/components/RequirePermission'
import { useRoles } from '@/hooks/usePermission'
import type { Package } from '@/types'

interface Props {
  tenantId: string | null
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  width: '100%',
}

// ── Package upload row ───────────────────────────────────────────────────────

function ArtifactUploadRow({ pkg }: { pkg: Package }) {
  const uploadArtifact = useUploadArtifact(pkg.id)
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    await uploadArtifact.mutateAsync(file)
  }

  if (pkg.artifact_uploaded) {
    return (
      <span style={{ color: 'var(--color-success)', fontSize: 12 }}>
        ✓ {pkg.artifact_filename} ({((pkg.artifact_size ?? 0) / 1024 / 1024).toFixed(2)} MB)
      </span>
    )
  }

  if (!pkg.hawkbit_distribution_set_id) {
    return <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>syncing…</span>
  }

  return (
    <>
      <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleUpload} />
      <button
        className="btn btn-ghost"
        style={{ fontSize: 12, padding: '4px 10px' }}
        disabled={uploadArtifact.isPending}
        onClick={() => fileRef.current?.click()}
      >
        {uploadArtifact.isPending ? 'Uploading…' : 'Upload artifact'}
      </button>
    </>
  )
}

// ── Packages tab ─────────────────────────────────────────────────────────────

function PackagesTab({ tenantId }: { tenantId: string | null }) {
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')
  const { data, isLoading, error } = usePackages()
  const createPackage = useCreatePackage()
  const deletePackage = useDeletePackage()
  const { data: deviceTypesData } = useDeviceTypes(tenantId)
  const deviceTypes = deviceTypesData?.data ?? []
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [version, setVersion] = useState('')
  const [description, setDescription] = useState('')
  const [deviceTypeId, setDeviceTypeId] = useState('')

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    await createPackage.mutateAsync({
      name: name.trim(),
      version: version.trim(),
      description: description.trim() || undefined,
      device_type_id: deviceTypeId || undefined,
    })
    setName('')
    setVersion('')
    setDescription('')
    setDeviceTypeId('')
    setShowCreate(false)
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Software Packages</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Software package catalog
          </p>
        </div>
        <RequirePermission perm="fleets:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>+ New package</button>
        </RequirePermission>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 20,
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 2fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Package name <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. telegraf" required style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Version <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input value={version} onChange={e => setVersion(e.target.value)} placeholder="e.g. 1.30.0" required style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Device type</label>
              <select value={deviceTypeId} onChange={e => setDeviceTypeId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">— All types —</option>
                {deviceTypes.map(dt => <option key={dt.id} value={dt.id}>{dt.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={createPackage.isPending}>
              {createPackage.isPending ? 'Creating…' : 'Create package'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
          <p style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
            After creation, upload the artifact file. The platform will process it automatically.
          </p>
        </form>
      )}

      {isLoading && <div className="empty-state">Loading…</div>}
      {error && <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading packages.</div>}

      {!isLoading && (data?.data ?? []).length === 0 && (
        <div className="empty-state">No packages yet. Create a package to start OTA deployments.</div>
      )}

      {(data?.data ?? []).length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Version</th>
                <th>Device type</th>
                <th>Artifact</th>
                {isPlatformAdmin && <th>HawkBit DS</th>}
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map(pkg => (
                <tr key={pkg.id}>
                  <td style={{ fontWeight: 500 }}>{pkg.name}</td>
                  <td><code style={{ fontSize: 12 }}>{pkg.version}</code></td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {pkg.device_type_id
                      ? (deviceTypes.find(dt => dt.id === pkg.device_type_id)?.name ?? pkg.device_type_id)
                      : '—'
                    }
                  </td>
                  <td><ArtifactUploadRow pkg={pkg} /></td>
                  {isPlatformAdmin && (
                    <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {pkg.hawkbit_distribution_set_id ?? '…'}
                    </td>
                  )}
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {new Date(pkg.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <RequirePermission perm="fleets:write">
                      <button
                        className="btn btn-danger"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => {
                          if (confirm(`Delete package ${pkg.name} ${pkg.version}?`)) {
                            deletePackage.mutate(pkg.id)
                          }
                        }}
                      >
                        Delete
                      </button>
                    </RequirePermission>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Rollout progress detail panel ─────────────────────────────────────────────

function RolloutProgressPanel({ tenantId, rolloutId, hawkbitRolloutId }: {
  tenantId: string
  rolloutId: string
  hawkbitRolloutId: string | undefined
}) {
  const { data, isLoading, error } = useRolloutProgress(
    tenantId,
    rolloutId,
    !!hawkbitRolloutId,
  )

  if (!hawkbitRolloutId) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--color-text-muted)' }}>
        Waiting for HawkBit to create the rollout…
      </div>
    )
  }
  if (isLoading) {
    return <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--color-text-muted)' }}>Loading progress…</div>
  }
  if (error) {
    return <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--color-danger)' }}>Failed to load progress.</div>
  }

  const p = data as RolloutProgress | undefined
  if (!p) return null

  const perStatus = p.totalTargetsPerStatus ?? {}
  const finished = perStatus['finished'] ?? 0
  const running = perStatus['running'] ?? 0
  const errored = perStatus['error'] ?? 0
  const scheduled = (perStatus['scheduled'] ?? 0) + (perStatus['notStarted'] ?? 0)
  const cancelled = perStatus['cancelled'] ?? 0
  const total = p.totalTargetsCount || 1

  const pct = p.finishedPercent ?? Math.round((finished / total) * 100)

  return (
    <div style={{
      padding: '14px 20px',
      background: 'var(--color-surface)',
      borderTop: '1px solid var(--color-border)',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      {/* progress bar */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          <span>Progress</span>
          <span>{pct}% — {p.totalTargetsCount} device{p.totalTargetsCount !== 1 ? 's' : ''}</span>
        </div>
        <div style={{
          height: 6,
          background: 'var(--color-border)',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
        }}>
          <div style={{ width: `${Math.min(pct, 100)}%`, background: errored > 0 ? 'var(--color-warning)' : 'var(--color-success)', transition: 'width 0.4s' }} />
        </div>
      </div>
      {/* per-status counters */}
      <div style={{ display: 'flex', gap: 20, fontSize: 12 }}>
        {finished > 0 && (
          <span style={{ color: 'var(--color-success)' }}>✓ {finished} finished</span>
        )}
        {running > 0 && (
          <span style={{ color: 'var(--color-primary)' }}>↻ {running} in progress</span>
        )}
        {scheduled > 0 && (
          <span style={{ color: 'var(--color-text-muted)' }}>⏳ {scheduled} scheduled</span>
        )}
        {errored > 0 && (
          <span style={{ color: 'var(--color-danger)' }}>✗ {errored} error</span>
        )}
        {cancelled > 0 && (
          <span style={{ color: 'var(--color-text-muted)' }}>⊘ {cancelled} cancelled</span>
        )}
        {Object.keys(perStatus).length === 0 && p.totalTargetsCount > 0 && (
          <span style={{ color: 'var(--color-text-muted)' }}>HawkBit status: {p.status}</span>
        )}
      </div>
    </div>
  )
}

// ── Rollouts tab ─────────────────────────────────────────────────────────────

function RolloutsTab({ tenantId }: Props) {
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')
  const { data: rolloutsData, isLoading } = useRollouts(tenantId)
  const { data: packagesData } = usePackages()
  const { data: fleetsData } = useFleets(tenantId)
  const createRollout = useCreateRollout(tenantId ?? '')
  const startRollout = useStartRollout(tenantId ?? '')
  const cancelRollout = useCancelRollout(tenantId ?? '')
  const [showCreate, setShowCreate] = useState(false)
  const [packageId, setPackageId] = useState('')
  const [fleetId, setFleetId] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const packages = (packagesData?.data ?? []).filter(p => p.hawkbit_distribution_set_id)
  const fleets = fleetsData?.data ?? []
  const rollouts = (rolloutsData as any)?.data ?? []

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!packageId) return
    await createRollout.mutateAsync({
      fleet_id: fleetId || undefined,
      params: { package_id: packageId },
    })
    setPackageId('')
    setFleetId('')
    setShowCreate(false)
  }

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage rollouts.</div>
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>OTA Rollouts</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Deploy software packages to device fleets
          </p>
        </div>
        <RequirePermission perm="fleets:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>+ New rollout</button>
        </RequirePermission>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 20,
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Package <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <select
                value={packageId}
                onChange={e => setPackageId(e.target.value)}
                required
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— Select package —</option>
                {packages.map(p => (
                  <option key={p.id} value={p.id}>{p.name} {p.version}</option>
                ))}
              </select>
              {packagesData && packages.length === 0 && (
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                  No packages with uploaded artifacts yet.
                </p>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Target fleet <span style={{ color: 'var(--color-text-muted)' }}>(optional — blank = all devices)</span>
              </label>
              <select
                value={fleetId}
                onChange={e => setFleetId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— All devices in tenant —</option>
                {fleets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={createRollout.isPending || !packageId}>
              {createRollout.isPending ? 'Creating…' : 'Create rollout'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
          <p style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
            The rollout will be created in <em>paused</em> state. Start it manually to begin distribution.
          </p>
        </form>
      )}

      {isLoading && <div className="empty-state">Loading…</div>}

      {!isLoading && rollouts.length === 0 && (
        <div className="empty-state">No rollouts yet.</div>
      )}

      {rollouts.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Status</th>
                <th>Fleet</th>
                {isPlatformAdmin && <th>HawkBit rollout</th>}
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rollouts.map((op: any) => {
                const isExpanded = expandedId === op.id
                const isTerminal = ['success', 'failed', 'cancelled'].includes(op.status)
                return (
                  <>
                    <tr
                      key={op.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedId(isExpanded ? null : op.id)}
                    >
                      <td style={{ width: 24, color: 'var(--color-text-muted)', fontSize: 11 }}>
                        {isExpanded ? '▾' : '▸'}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{op.id.slice(0, 8)}…</td>
                      <td><span className={`badge badge-${op.status}`}>{op.status}</span></td>
                      <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {op.fleet_id ? (fleets.find((f: any) => f.id === op.fleet_id)?.name ?? op.fleet_id) : 'all devices'}
                      </td>
                      {isPlatformAdmin && (
                        <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                          {op.hawkbit_rollout_id ?? '…'}
                        </td>
                      )}
                      <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        {new Date(op.created_at).toLocaleDateString()}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {/* Start: show when hawkbit rollout exists but op is still pending (not yet started) */}
                          {op.hawkbit_rollout_id && op.status === 'pending' && (
                            <RequirePermission perm="fleets:write">
                              <button
                                className="btn btn-primary"
                                style={{ padding: '4px 10px', fontSize: 12 }}
                                disabled={startRollout.isPending}
                                onClick={() => startRollout.mutate(op.id)}
                              >
                                Start
                              </button>
                            </RequirePermission>
                          )}
                          {!isTerminal && (
                            <RequirePermission perm="fleets:write">
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 10px', fontSize: 12 }}
                                disabled={cancelRollout.isPending}
                                onClick={() => {
                                  if (confirm('Cancel this rollout?')) cancelRollout.mutate(op.id)
                                }}
                              >
                                Cancel
                              </button>
                            </RequirePermission>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${op.id}-detail`}>
                        <td colSpan={isPlatformAdmin ? 7 : 6} style={{ padding: 0, borderTop: 'none' }}>
                          <RolloutProgressPanel
                            tenantId={tenantId}
                            rolloutId={op.id}
                            hawkbitRolloutId={op.hawkbit_rollout_id}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Main OTA page ─────────────────────────────────────────────────────────────

type Tab = 'packages' | 'rollouts'

export function OTA({ tenantId }: Props) {
  const [tab, setTab] = useState<Tab>('packages')

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: tab === t ? 600 : 400,
    color: tab === t ? 'var(--color-primary)' : 'var(--color-text-muted)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderBottomWidth: 2,
    borderBottomStyle: 'solid',
    borderBottomColor: tab === t ? 'var(--color-primary)' : 'transparent',
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">OTA Updates</h1>
          <p className="page-subtitle">Manage software packages and deploy them to device fleets</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--color-border)', marginBottom: 24 }}>
        <button style={tabStyle('packages')} onClick={() => setTab('packages')}>Packages</button>
        <button style={tabStyle('rollouts')} onClick={() => setTab('rollouts')}>Rollouts</button>
      </div>

      {tab === 'packages' && <PackagesTab tenantId={tenantId} />}
      {tab === 'rollouts' && <RolloutsTab tenantId={tenantId} />}
    </div>
  )
}
